import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Sparkles, Settings, ClipboardList, ClipboardCheck, AlertTriangle, BarChart3, Bot, Link2 } from "lucide-react";
import axios from "axios";
import { API } from "../../config/api";

import MLCOQueuePanel from "../../components/compliance/MLCOQueuePanel";
import COLPReviewPanel from "../../components/compliance/COLPReviewPanel";
import CCLBlockedFeed from "../../components/compliance/CCLBlockedFeed";
import ComplianceSettingsPanel from "../../components/compliance/ComplianceSettingsPanel";
import FWRAAggregationsPanel from "../../components/compliance/FWRAAggregationsPanel";
import AIDataFlowPanel from "../../components/compliance/AIDataFlowPanel";
import EvidencePackPanel from "../../components/compliance/EvidencePackPanel";
import FirmRiskPanel from "../../components/compliance/FirmRiskPanel";
import MLROAlertsPanel from "../../components/compliance/MLROAlertsPanel";
import PolicyBindingsPanel from "../../components/compliance/PolicyBindingsPanel";
// BM-H643: live matters with no risk assessment.
import RiskAssessmentGapsPanel from "../../components/compliance/RiskAssessmentGapsPanel";
// 26 Oct 2026: the old /policy/dashboard/mlco page now lives here as a tab.
import MLCODashboard from "./MLCODashboard";

/**
 * Brief 14d — MLCO + COLP Compliance Dashboard.
 *
 * Role gate: in-component early-return. MLCO sees Panel A only;
 * COLP sees Panel A + Panel B + Panel C + the settings panel.
 */
// The panels sit on five tabs so the page reads one job at a time. Every
// panel stays mounted (hidden, not unmounted) so their own fetches and
// tests behave as before. Hues are never green, amber or red (status).
const TABS = [
  { key: "now", label: "Needs you", icon: AlertTriangle, hue: ["#6366f1", "#a855f7"] },
  { key: "aml", label: "AML & attestations", icon: ClipboardCheck, hue: ["#ec4899", "#8b5cf6"] },
  { key: "risk", label: "Firm risk", icon: BarChart3, hue: ["#3b82f6", "#06b6d4"] },
  { key: "ai", label: "AI & evidence", icon: Bot, hue: ["#d946ef", "#ec4899"] },
  { key: "bindings", label: "Policy bindings", icon: Link2, hue: ["#8b5cf6", "#3b82f6"] },
];

export default function ComplianceDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const setTab = (key) => setSearchParams(key === "now" ? {} : { tab: key }, { replace: true });
  const [showSettings, setShowSettings] = useState(false);
  const [userMeta, setUserMeta] = useState(null);
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  // Brief 14e — open COLP reviews surfaced into Panel B for PDF generation
  const [colpReviews, setColpReviews] = useState([]);

  const authHeaders = useCallback(() => {
    const email =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    return { "X-Bonsai-User-Id": email };
  }, []);

  // 1. Fetch role meta once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/user-role`, { headers: authHeaders() });
        if (!cancelled) setUserMeta(res.data || {});
      } catch (err) {
        // nosilent: dashboard cannot operate without role meta;
        // surface as empty role set → page redirects to /dashboard.
        // eslint-disable-next-line no-console
        console.warn("ComplianceDashboard: user-role fetch failed", err);
        if (!cancelled) setUserMeta({});
      } finally {
        if (!cancelled) setMetaLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authHeaders]);

  // 2. Fetch summary on mount (once role meta is in and user is eligible).
  useEffect(() => {
    if (!metaLoaded || !userMeta) return;
    if (!userMeta.is_mlco && !userMeta.is_colp) return;
    let cancelled = false;
    (async () => {
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const res = await axios.get(
          `${API}/compliance/dashboard/summary`,
          { headers: authHeaders() }
        );
        if (!cancelled) setSummary(res.data || {});
      } catch (err) {
        if (!cancelled) setSummaryError(err?.message || "summary fetch failed");
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [metaLoaded, userMeta, authHeaders]);

  // Brief 14e — fetch the COLP review list for the snapshot generator.
  // Uses the existing 14b/14c-i ``matter-reviews`` endpoint (filtered by
  // review_type=colp_signoff). Quietly tolerated if endpoint is absent
  // in dev (logged once; the button list simply doesn't render).
  useEffect(() => {
    if (!metaLoaded || !userMeta?.is_colp) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/compliance/matter-reviews?review_type=colp_signoff&limit=50`,
          { headers: authHeaders() }
        );
        const rows = Array.isArray(res.data?.reviews) ? res.data.reviews : [];
        const mapped = rows
          .map((r) => ({
            review_id: r._id || r.id || "",
            matter_ref: r.matter_ref || r.matter_reference || "",
          }))
          .filter((r) => r.review_id && r.matter_ref);
        if (!cancelled) setColpReviews(mapped);
      } catch (err) {
        // nosilent: surface as empty list; log once for ops visibility.
        // eslint-disable-next-line no-console
        console.warn("ComplianceDashboard: colp reviews fetch failed", err);
        if (!cancelled) setColpReviews([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [metaLoaded, userMeta, authHeaders]);

  if (!metaLoaded) {
    return (
      <div className="p-4 sm:p-6" data-testid="compliance-dashboard-loading">
        <div className="h-4 w-40 bg-slate-800 rounded animate-pulse" />
      </div>
    );
  }

  // MLCO and COLP see every tab. A director who is neither sees only the
  // AML & attestations tab: it was the old /policy/dashboard/mlco page,
  // whose write routes admit the MLCO or a director.
  const isOfficer = !!(userMeta?.is_mlco || userMeta?.is_colp);
  if (!isOfficer && !userMeta?.is_director) {
    return <Navigate to="/dashboard" replace />;
  }

  const isColp = !!userMeta?.is_colp;
  const tabs = isOfficer ? TABS : TABS.filter((t) => t.key === "aml");
  const tab = tabs.some((t) => t.key === requestedTab) ? requestedTab : tabs[0].key;

  const q = summary?.mlco_queue || {};
  const needsYou = (q.open_count || 0) + (q.mlco_reviewing_count || 0) + (isColp ? (summary?.colp_queue?.open_count || 0) : 0);
  const show = (key) => (tab === key ? "space-y-4" : "hidden");

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-screen-xl mx-auto" data-testid="compliance-dashboard">
      {/* Header band: what this page is, and the tabs. */}
      <div className="cm-hero relative overflow-hidden rounded-2xl border border-white/10 px-5 pt-4 pb-3 shadow-xl shadow-indigo-950/40">
        <div aria-hidden className="agents-orb absolute -top-24 -right-16 w-72 h-72 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(236,72,153,0.28), transparent 65%)" }} />
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative w-11 h-11 rounded-xl flex items-center justify-center ring-1 ring-white/20 shadow-lg shadow-indigo-900/50"
              style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6 55%, #3b82f6)" }}>
              <ShieldCheck size={22} className="text-white" />
              <Sparkles size={12} className="agents-twinkle absolute -top-1 -right-1 text-amber-200" />
            </div>
            <div>
              <h1 className="text-xl font-bold agents-title leading-tight">Compliance Dashboard</h1>
              <p className="text-xs text-indigo-100/75">
                {isColp ? "MLCO and COLP" : isOfficer ? "MLCO" : "Director"} · what needs a decision, then the firm-wide picture
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* BM-H632 — the File Review Register and form. */}
            {isOfficer && (
            <Link
              to="/compliance/file-reviews"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white cm-glass ring-1 ring-white/20 cm-glass-hover"
              data-testid="compliance-dashboard-file-reviews-link"
            >
              <ClipboardList size={13} /> File reviews
            </Link>
            )}
            {isColp && (
              <button
                type="button"
                onClick={() => setShowSettings((v) => !v)}
                title="Compliance settings"
                aria-pressed={showSettings}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white ring-1 ring-white/20 cm-glass-hover ${showSettings ? "cm-glass-strong" : "cm-glass"}`}
                data-testid="compliance-settings-toggle"
              >
                <Settings size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="relative flex flex-wrap gap-1.5 mt-4" role="tablist" data-testid="compliance-tabs">
          {tabs.map(({ key, label, icon: Icon, hue }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active ? "text-white shadow-md" : "text-indigo-100/75 hover:text-white cm-glass-hover"}`}
                style={active ? { background: `linear-gradient(90deg, ${hue[0]}, ${hue[1]})` } : undefined}
                data-testid={`compliance-tab-${key}`}
              >
                <Icon size={13} />
                {label}
                {key === "now" && needsYou > 0 && (
                  <span className="ml-0.5 px-1.5 rounded-full text-[10px] tabular-nums cm-glass-strong">{needsYou}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {isOfficer && (
        <>
      {isColp && (
        <div className={showSettings ? "" : "hidden"}>
          <ComplianceSettingsPanel userMeta={userMeta} />
        </div>
      )}

      <div className={show("now")}>
        <RiskAssessmentGapsPanel authHeaders={authHeaders} />
        <div className="grid gap-4 lg:grid-cols-2 items-start">
          <MLCOQueuePanel
            data={summary?.mlco_queue}
            loading={summaryLoading}
            error={summaryError}
            userMeta={userMeta}
          />
          {isColp && (
            <COLPReviewPanel
              data={summary?.colp_queue}
              loading={summaryLoading}
              error={summaryError}
              reviews={colpReviews}
            />
          )}
        </div>
        {/* R&D-E Gap 4 — MLRO alerts panel (server-side gated). */}
        <MLROAlertsPanel authHeaders={authHeaders} />
        {isColp && <CCLBlockedFeed />}
      </div>

        </>
      )}

      {/* Mounted only while open: it polls every 60s. */}
      {tab === "aml" && <MLCODashboard embedded />}

      {isOfficer && (
        <>
      <div className={show("risk")}>
        {/* R&D-E Gap 1 — firm-wide risk aggregation (server-side gated). */}
        <FirmRiskPanel authHeaders={authHeaders} />
        {/* Brief 14g — Panel D: FWRA aggregations (COLP only in v1). */}
        {isColp && <FWRAAggregationsPanel authHeaders={authHeaders} />}
      </div>

      <div className={show("ai")}>
        {/* RnD-C — AI data-flow audit surface (all compliance viewers). */}
        <AIDataFlowPanel authHeaders={authHeaders} />
        {/* RnD-D — evidence pack (director/admin/MLRO; server-side gated). */}
        <EvidencePackPanel authHeaders={authHeaders} />
      </div>

      <div className={show("bindings")}>
        {/* R&D-F1 — policy-code bindings admin (server-side gated). */}
        <PolicyBindingsPanel authHeaders={authHeaders} />
      </div>
        </>
      )}
    </div>
  );
}
